import React, { FormEvent, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, FormControlLabel, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import { IEmployeeDetails } from '../../types/employee';

import { EmployeeService } from '../../services/employeeService';

import { useListContext } from '../../contexts/listContext';

import '../../styles/employeeDetailsForm.scss';
import { AppConfig } from '../../config/appConfig';

export interface IEmployeeDetailsForm {
	action: "add" | "edit";
	id?: string;
	details: IEmployeeDetails;
	reloadData: () => void;
}

const EmployeeDetailsForm: React.FunctionComponent<IEmployeeDetailsForm> = ({ action, id, details, reloadData }) => {
	const navigate = useNavigate();
	const listContext = useListContext();
	const [formFields, setFormFields] = useState<IEmployeeDetails>(details);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();
	const [isDashboardIdValid, setIsDashboardIdValid] = useState<boolean>(true);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		clearSaveResult();
		setIsSaving(true);

		if (formFields.homeDashboard == '') {
			formFields.homeDashboard = undefined;
		}

		if (action == "add") {
			EmployeeService.addEmployee(formFields)
				.then((response) => {
					console.log('EmployeeService.addEmployee: SUCCESS', response);
					setSuccessMessage("Employee has been added.");
					setTimeout(() => {
						navigate("/employee/" + response.data.id);
					}, AppConfig.addSuccessToastMs);
				})
				.catch((error) => {
					console.log('EmployeeService.addEmployee: ERROR', error);
					setErrorMessage("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsSaving(false);
					window.scrollTo(0, 0);
				});
		}
		else if (action == "edit" && id) {
			EmployeeService.setDetails(id, formFields)
				.then((response) => {
					console.log('EmployeeService.setDetails: SUCCESS', response);
					reloadData();
					setSuccessMessage("Employee details have been saved.");
					setTimeout(() => {
						setSuccessMessage(undefined);
					}, AppConfig.editSuccessToastMs);
				})
				.catch((error) => {
					console.log('EmployeeService.setDetails: ERROR', error);
					setErrorMessage("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsSaving(false);
					window.scrollTo(0, 0);
				});
		}
	}

	const validateDashboardId = (dashboardId: string) => {
		let valid = true;
		const content = dashboardId?.replace(/-/g, '')?.trim() ?? '';
		if (content.length > 0 && content.length < 32) valid = false;   // GUIDs are 32 chars without dashes
		if (content.length && content.search(/^(?:[0-9a-fA-F]{32})$/) < 0) valid = false; // GUIDs contain only hex characters
		setIsDashboardIdValid(valid);

		if (valid) {
			setFormFields({ ...formFields, ["homeDashboard"]: content.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5').toLowerCase() });
		}
	}

	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	return (
		<section className="hwi-employee-details-form">
			{successMessage &&
				<Alert className="alert-success"
					severity="success"
					action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
					{successMessage}
				</Alert>
			}
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			<Form onSubmit={(event) => handleSubmit(event)}>
				<div className="columns">
					<div className="col">
						<div className="row">
							<InputLabel>First Name *</InputLabel>
							<TextField className="first-name-field" required name="firstName" value={formFields.firstName} size="small" onChange={(event) => { setFormFields({ ...formFields, ["firstName"]: event.target.value }) }}></TextField>
						</div>
						<div className="row">
							<InputLabel>Last Name *</InputLabel>
							<TextField className="last-name-field" required name="lastName" value={formFields.lastName} size="small" onChange={(event) => { setFormFields({ ...formFields, ["lastName"]: event.target.value }) }}></TextField>
						</div>
						<div className="row">
							<InputLabel>AD Username *</InputLabel>
							<TextField className="ad-username-field" required name="adUserName" value={formFields.adUserName} size="small" onChange={(event) => { setFormFields({ ...formFields, ["adUserName"]: event.target.value }) }}></TextField>
						</div>
						<div className="row">
							<InputLabel>Usergroup</InputLabel>
							<TextField className="usergroup-field" name="userGroup" value={formFields.userGroup} size="small" onChange={(event) => { setFormFields({ ...formFields, ["userGroup"]: event.target.value }) }}></TextField>
						</div>
						<div className="row">
							<InputLabel>Title</InputLabel>
							<TextField className="title-field" name="title" value={formFields.title} size="small" onChange={(event) => { setFormFields({ ...formFields, ["title"]: event.target.value }) }}></TextField>
						</div>
						<div className="row">
							<InputLabel>Email *</InputLabel>
							<TextField className="email-field" name="email" value={formFields.email} size="small" onChange={(event) => { setFormFields({ ...formFields, ["email"]: event.target.value }) }}></TextField>
						</div>
						<div className="row">
							<InputLabel>Business Phone</InputLabel>
							<TextField className="business-phone-field" name="businessPhone" value={formFields.businessPhone} size="small" onChange={(event) => { setFormFields({ ...formFields, ["businessPhone"]: event.target.value }) }}></TextField>
						</div>
						<div className="row">
							<InputLabel>Personal Phone</InputLabel>
							<TextField className="personal-phone-field" name="personalPhone" value={formFields.personalPhone} size="small" onChange={(event) => { setFormFields({ ...formFields, ["personalPhone"]: event.target.value }) }}></TextField>
						</div>
						{/* <div className="row">
						<InputLabel>Home Dashboard</InputLabel>
						<Select
							className="payee-field"
							value={formFields.homeDashboard ?? undefined}

							onChange={(event) => { setFormFields({ ...formFields, ["homeDashboard"]: event.target.value }) }}
						>
							<MenuItem value={undefined} key={0}>None</MenuItem>
							<MenuItem value={'fdac4c63-7ff8-4f95-9c62-49c7e0a610e8'} key={1} >Executive Dashboard</MenuItem>
							<MenuItem value={'9501ac5e-ffb3-4133-b3e6-d80f743c75d8'} key={2} >Site Manager Dashboard</MenuItem>
						</Select>
					</div> */}
						
					</div>
					<div className="col">
						<div className="row">
							<InputLabel>Home Layout Position 1</InputLabel>
							<Select
								className="home-layout-position-field"
								value={formFields.homeLayoutPosition1 ?? undefined}
								onChange={(event) => { setFormFields({ ...formFields, ["homeLayoutPosition1"]: event.target.value }) }}
							>
								<MenuItem value='Empty' key={0}>Empty</MenuItem>
								<MenuItem value='Closing Status' key={0}>Closing Status</MenuItem>
								<MenuItem value='Executive Dashboard' key={1} >Executive Dashboard</MenuItem>
								<MenuItem value='Favorites' key={0}>Favorites</MenuItem>
								<MenuItem value='Site Manager Dashboard' key={2} >Site Manager Dashboard</MenuItem>
								<MenuItem value='Waiting Shipments' key={0}>Waiting Shipments</MenuItem>
							</Select>
						</div>
						<div className="row">
							<InputLabel>Home Layout Position 2</InputLabel>
							<Select
								className="home-layout-position-field"
								value={formFields.homeLayoutPosition2 ?? undefined}
								onChange={(event) => { setFormFields({ ...formFields, ["homeLayoutPosition2"]: event.target.value }) }}
							>
								<MenuItem value='Empty' key={0}>Empty</MenuItem>
								<MenuItem value='Closing Status' key={0}>Closing Status</MenuItem>
								<MenuItem value='Executive Dashboard' key={1} >Executive Dashboard</MenuItem>
								<MenuItem value='Favorites' key={0}>Favorites</MenuItem>
								<MenuItem value='Site Manager Dashboard' key={2} >Site Manager Dashboard</MenuItem>
								<MenuItem value='Waiting Shipments' key={0}>Waiting Shipments</MenuItem>
							</Select>
						</div>
						<div className="row">
							<InputLabel>Home Layout Position 3</InputLabel>
							<Select
								className="home-layout-position-field"
								value={formFields.homeLayoutPosition3 ?? undefined}
								onChange={(event) => { setFormFields({ ...formFields, ["homeLayoutPosition3"]: event.target.value }) }}
							>
								<MenuItem value='Empty' key={0}>Empty</MenuItem>
								<MenuItem value='Closing Status' key={0}>Closing Status</MenuItem>
								<MenuItem value='Executive Dashboard' key={1} >Executive Dashboard</MenuItem>
								<MenuItem value='Favorites' key={0}>Favorites</MenuItem>
								<MenuItem value='Site Manager Dashboard' key={2} >Site Manager Dashboard</MenuItem>
								<MenuItem value='Waiting Shipments' key={0}>Waiting Shipments</MenuItem>
							</Select>
						</div>
						<div className="row">
							<InputLabel>Home Layout Position 4</InputLabel>
							<Select
								className="home-layout-position-field"
								value={formFields.homeLayoutPosition4 ?? undefined}
								onChange={(event) => { setFormFields({ ...formFields, ["homeLayoutPosition4"]: event.target.value }) }}
							>
								<MenuItem value='Empty' key={0}>Empty</MenuItem>
								<MenuItem value='Closing Status' key={0}>Closing Status</MenuItem>
								<MenuItem value='Executive Dashboard' key={1} >Executive Dashboard</MenuItem>
								<MenuItem value='Favorites' key={0}>Favorites</MenuItem>
								<MenuItem value='Site Manager Dashboard' key={2} >Site Manager Dashboard</MenuItem>
								<MenuItem value='Waiting Shipments' key={0}>Waiting Shipments</MenuItem>
							</Select>
						</div>
						<div className="row">
							<InputLabel>Home Layout Position 5</InputLabel>
							<Select
								className="home-layout-position-field"
								value={formFields.homeLayoutPosition5 ?? undefined}
								onChange={(event) => { setFormFields({ ...formFields, ["homeLayoutPosition5"]: event.target.value }) }}
							>
								<MenuItem value='Empty' key={0}>Empty</MenuItem>
								<MenuItem value='Closing Status' key={0}>Closing Status</MenuItem>
								<MenuItem value='Executive Dashboard' key={1} >Executive Dashboard</MenuItem>
								<MenuItem value='Favorites' key={0}>Favorites</MenuItem>
								<MenuItem value='Site Manager Dashboard' key={2} >Site Manager Dashboard</MenuItem>
								<MenuItem value='Waiting Shipments' key={0}>Waiting Shipments</MenuItem>
							</Select>
						</div>
						<div className="row">
							<FormControlLabel control={<Checkbox name="canDeleteShipments" checked={formFields.isActive} onChange={(event) => setFormFields({ ...formFields, ["isActive"]: event.target.checked })}></Checkbox>} label="Employee Is Active" />
						</div>
					</div>
				</div>

				<div className="row controls">
					<Button type="submit" className="save-button" disabled={isSaving || !isDashboardIdValid} variant="contained">{isSaving ? "Saving..." : "Save Details"}</Button>
				</div>
			</Form>
		</section>
	)
}

export default EmployeeDetailsForm;